import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import Textfield from '@atlaskit/textfield';
import { FormValues, ChampionPicklistField } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  howHeard?: string;
  howHeardOther?: string;
  howHeardEmployee?: string;
  referrerName?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
  howHeardOptions: ChampionPicklistField['picklistValues'];
};

export const HowHeardSection: React.FC<Props> = ({ values, updateField, errors, howHeardOptions }) => {
  const setHowHeard = (value: string) => () => updateField('howHeard', value);

  const showOtherField = values.howHeard === 'Other';
  const showEmployeeField = values.howHeard === 'Atlassian Employee (Other than EA or TAM)';

  return (
    <div css={sectionBoxStyles}>
      <div
        css={fieldStyles}
        className={
          errors?.howHeard
            ? 'champion-form-how-heard champion-form-how-heard--error'
            : 'champion-form-how-heard'
        }
      >
        <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>{' '}
          How did you find out about the Atlassian Champions program?
        </label>

        <div css={controlTopStyles}>
          {howHeardOptions.map((opt) => (
            <Radio
              key={opt.value}
              name="how_heard"
              label={opt.label}
              value={opt.value}
              isChecked={values.howHeard === opt.value}
              onChange={setHowHeard(opt.value)}
            />
          ))}
        </div>

        {errors?.howHeard && <div css={errorStyles}>{errors.howHeard}</div>}
      </div>

      {showOtherField && (
        <div css={fieldTopStyles} className="champion-form-how-heard-other">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="how-heard-other">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Please specify
          </label>
          <Textfield
            id="how-heard-other"
            value={values.howHeardOther}
            onChange={(e) => updateField('howHeardOther', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.howHeardOther}
            maxLength={255}
          />
          {errors?.howHeardOther && <div css={errorStyles}>{errors.howHeardOther}</div>}
        </div>
      )}

      {showEmployeeField && (
        <div css={fieldTopStyles} className="champion-form-how-heard-employee">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="how-heard-employee">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Please specify
          </label>
          <Textfield
            id="how-heard-employee"
            value={values.howHeardEmployee}
            onChange={(e) => updateField('howHeardEmployee', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.howHeardEmployee}
            maxLength={255}
          />
          {errors?.howHeardEmployee && <div css={errorStyles}>{errors.howHeardEmployee}</div>}
        </div>
      )}

      {values.howHeard === 'Atlassian Community Leader' && (
        <div css={fieldTopStyles} className="champion-form-how-heard-referrer">
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="referrer-name">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Referrer Name
          </label>
          <Textfield
            id="referrer-name"
            value={values.referrerName}
            maxLength={80}
            onChange={(e) => updateField('referrerName', (e.target as HTMLInputElement).value)}
            isCompact
            isInvalid={!!errors?.referrerName}
          />
          {errors?.referrerName && <div css={errorStyles}>{errors.referrerName}</div>}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '40px',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const labelBoldStyles = css({
  fontWeight: 600,
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
